import React, { memo, useCallback, useMemo, Fragment } from 'react';
import { useSelector } from 'react-redux';
import { Col, Container, Row } from 'reactstrap';
import { get } from 'lodash-es';
import { FeatureFlag } from 'site-modules/shared/components/feature-flag/feature-flag';
import { UsurpStickyHeaderSemanticSearch } from 'site-modules/inventory/components/usurp-sticky-header-semantic-search/usurp-sticky-header-semantic-search';
import { SKIP_TO_LINKS_CONFIG, SRP_BASE_PATHNAME } from 'site-modules/shared/constants/inventory/routes-constants';
import { ScrollLink } from 'site-modules/shared/components/scroll-link/scroll-link';
import { GLOBAL_HEADER_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { ZipCodeReminder } from 'site-modules/inventory/components/srp/zip-code-reminder/zip-code-reminder';
import { IcoReducedHeightStandard } from 'site-modules/shared/components/dealer-ico-widget/ico-reduced-height-standard/ico-reduced-height-standard';
import { IcoWidget } from 'client/site-modules/shared/components/dealer-ico-widget/ico-widget/ico-widget';
import { SrpTopMessage } from 'site-modules/inventory/components/srp-top-message/srp-top-message';
import { UsurpCardList } from 'site-modules/shared/components/inventory/usurp-card-list/usurp-card-list';
import { InventoriesNotFoundWithLinks } from 'site-modules/shared/components/inventory/inventories-not-found-with-links/inventories-not-found-with-links';
import { SrpPagination } from 'site-modules/shared/components/inventory/srp-pagination/srp-pagination';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { InventoryAdTargetingPrefetch } from 'site-modules/shared/components/native-ad/inventory-ad-targeting/inventory-ad-targeting-prefetch';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { NativeAd } from 'site-modules/shared/components/ad-unit/native-ad';
import { ResearchPageLink } from 'site-modules/inventory/components/research-page-link/research-page-link';
import { ConsumerReviewsWidget } from 'site-modules/inventory/components/consumer-reviews-widget/consumer-reviews-widget';
import { isCpo } from 'site-modules/shared/utils/inventory-utils/is-cpo';
import { CertifiedPreOwned } from 'site-modules/shared/components/certified-pre-owned/certified-pre-owned';
import { GreatDealsLinkWidgetTable } from 'site-modules/inventory/components/great-deals-link-widget-table/great-deals-link-widget-table';
import { LeaseDealsLinkWidgetTable } from 'site-modules/inventory/components/lease-deals-link-widget-table/lease-deals-link-widget-table';
import {
  isLeaseSRP,
  isNewFlatUrlSRP,
  isUsedYMMCityStateSRP,
} from 'site-modules/shared/utils/inventory/srp-type-checkers';
import { InventoryDataTable } from 'site-modules/inventory/components/inventory-data-table/inventory-data-table';
import { ErrorBoundary } from 'site-modules/shared/components/error-boundary/error-boundary';
import { FaqModule } from 'site-modules/inventory/components/faq-module/faq-module';
import { AppraisalSection } from 'site-modules/inventory/components/appraisal-section/appraisal-section';
import { UsurpFiltersPanel } from 'site-modules/shared/components/inventory/usurp-filters-panel/usurp-filters-panel';
import { UsurpFilters } from 'site-modules/shared/components/inventory/usurp-filters/usurp-filters';
import { ELECTRICITY_RANGE, SORT_BY } from 'site-modules/shared/constants/allowed-inventory-request-params';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { SrpDisclaimer } from 'site-modules/inventory/components/srp-disclaimer/srp-disclaimer';
import { SrpCardsTopPanel } from 'site-modules/shared/components/inventory/srp-cards-top-panel/srp-cards-top-panel';
import { expandedRadiusDueToLowResultsSort } from 'site-modules/shared/constants/sort-types';
import { UsurpInventoryCard } from 'site-modules/shared/components/inventory/usurp-inventory-card/usurp-inventory-card';
import { EvEBuyingExperienceButtonGroup } from 'site-modules/shared/components/eve-buying-experience-button-group/eve-buying-experience-button-group';
import { ANY_BTN, IN_STORE_BTN } from 'site-modules/shared/constants/inventory/buying-experience';
import { PUB_STATES_LOWERCASE } from 'client/constants/pub-states';
import {
  getLinkWidgetTitle,
  getPageName,
  getPagePublicationState,
  getUrlPattern,
} from 'site-modules/shared/utils/inventory/srp-utils';
import { SearchFilterSeoObjectEntities } from 'site-modules/shared/utils/inventory/srp-seo-utils';
import { getSrpBreadcrumbs } from 'site-modules/shared/utils/inventory/srp-breadcrumbs';
import { getHasLinkWidget } from 'site-modules/inventory/utils/link-widget';
import PropTypes from 'prop-types';
import { InventoryEntities } from 'client/data/models/inventory';
import { VisitorEntities } from 'client/data/models/visitor';
import { ZipLongDistanceConfirmProvider } from 'site-modules/shared/components/lead-form/zip-long-distance-confirm/zip-long-distance-confirm-provider';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { SponsoredCars } from 'site-modules/shared/components/core-page/sponsored-cars/sponsored-cars';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import { PaginationLink } from 'site-modules/shared/components/pagination-link/pagination-link';
import { PaginationButton } from 'site-modules/shared/components/pagination-button/pagination-button';
import { VERTICAL_ALIGN } from 'site-modules/shared/components/ad-unit/ad-unit-prop-types';
import { MRECT_WIRED_CUSTOM_SIZE } from 'site-modules/shared/components/ad-unit/context/sizes';
import { Shop3947Experiment } from 'site-modules/shared/components/shop-3947-experiment/shop-3947-experiment';
import { CoDriverBanner } from 'site-modules/shared/components/inventory/co-driver-card/co-driver-banner';
import { IgnoreRenderWhenCoDriver } from 'site-modules/shared/components/co-driver/ignore-render-when-co-driver/ignore-render-when-co-driver';
import { Shop5016Backfill } from 'site-modules/inventory/components/shop-5016-backfill/shop-5016-backfill';
import { SHOP_5016_CAMPAIGN, SHOP_5016_STRATEGIES } from 'site-modules/inventory/utils/shop-5016-backfill-utils';
import {
  SrpBuyOnlinePromo,
  SrpLocalListingsCheckbox,
} from 'site-modules/inventory/components/shop-5067-srp-top/shop-5067-srp-top';
import {
  getShop5067SrpTopRecipe,
  isShop5067SrpTopEnabled,
} from 'site-modules/inventory/components/shop-5067-srp-top/shop-5067-srp-top-utils';
import { FilterButton } from 'site-modules/shared/components/inventory/filters-button/filters-button';
import { transformSelectedFacetsForDisplaying } from 'site-modules/shared/utils/inventory/search-filter';

export const UsurpMainContent = memo(
  ({
    disableInventoryMLPhotoSort = false,
    isNational = false,
    inventoryData,
    visitorLocation = {},
    paymentType = '',
    selectedPaymentType = '',
    radius,
    isDealerSrp = false,
    isMobile,
    facets = undefined,
    selectedFacets,
    seoData,
    filteringByFacets = [],
    onOpenFiltersDrawer,
    onSuggestedFacetUpdate,
    onUpdateFilter,
    zipCode,
    onUpdateZipCode,
    pathName = '',
    userCapOnePrequalApproved = false,
    urlContext = {},
    onRemoveCapOnePrequalParam,
    onMultiMMChange,
    onPageChange,
    year = undefined,
    submodel = undefined,
    isVinPreview = false,
    disableDrivewayNewCarLeadForms = false,
    cpoData = null,
    isCrawler = false,
    onApplyFilterToUrl = undefined,
    isSeot4897Enabled = false,
    isShop5157Challenger = false,
  }) => {
    const isExpandedRadiusDueToLowResults = !!get(inventoryData, 'attributes.expandedRadiusDueToLowResults', false);
    const attrRadius = get(inventoryData, 'attributes.radius');
    const isExpandedRadiusDueToLowResultsSort = isExpandedRadiusDueToLowResults && attrRadius === 6000;
    const {
      totalPages,
      pageSize,
      pageNumber,
      totalNumber: inventoryCount,
      maxAllowedPageNumber,
    } = get(inventoryData, 'inventories', {});
    const attributes = get(inventoryData, 'attributes', {});
    const inventoryType = get(attributes, 'inventoryType', '');
    const suggestedFacets = get(inventoryData, 'suggestedFacets', []);
    const hasBuyOnlineInventories = get(attributes, 'buyOnlineCount', 0) > 0;
    const isLocalDeliveryType = get(selectedFacets, 'deliveryType[0]') === 'local';
    const selectedBtn = isLocalDeliveryType ? IN_STORE_BTN : ANY_BTN;
    const selectedCarTypes = get(selectedFacets, 'inventoryType', []);
    const pagePublicationState = getPagePublicationState(selectedCarTypes);
    const isUsed = pagePublicationState === PUB_STATES_LOWERCASE.USED;
    const dealerId = get(attributes, 'dealerInfo.rooftopId');
    const inventory = get(inventoryData, 'inventories.results', []);
    const make = get(attributes, 'vehicle.make.name', '');
    const displayLowInvModule = inventoryCount <= 5;
    const assignedShop5067ExperimentVariant = useSelector(getShop5067SrpTopRecipe);
    const shop5067ExperimentVariant = isVinPreview ? 'ctrl' : assignedShop5067ExperimentVariant;
    const isShop5067SrpTopChallenger = isShop5067SrpTopEnabled(shop5067ExperimentVariant);
    const activeFiltersCount = transformSelectedFacetsForDisplaying(filteringByFacets).length;

    const attributesVehicle = useMemo(() => {
      const { vehicle } = attributes ?? {};

      return vehicle?.make
        ? {
            ...vehicle,
            make: { ...vehicle.make, slug: vehicle.make.niceId },
            model: vehicle.model ? { ...vehicle.model, slug: vehicle.model.niceId } : vehicle.model,
            type: attributes.inventoryType,
          }
        : null;
    }, [attributes]);

    const consumerReviews = get(inventoryData, 'seoInfo.consumerReviews');
    const { latestYear } = seoData;
    const greatDealsWidgetData = get(inventoryData, 'seoInfo.greatDealsWidget');
    const leaseDealsWidgetData = get(inventoryData, 'seoInfo.leaseDealsWidget');
    const urlPath = getUrlPattern(urlContext);
    const pageName = getPageName(urlPath);
    const faqApiData = get(inventoryData, 'seoInfo.faqWidget');
    const hasFaqApiData = !!faqApiData && faqApiData.items.length > 0;
    const usedYearAppraisalWidget = isUsedYMMCityStateSRP(urlPath)
      ? get(inventoryData, 'seoInfo.usedYearAppraisalWidget')
      : {};
    const isLease = isLeaseSRP(urlContext);
    const linkWidgetTitle = getLinkWidgetTitle({ isLease, urlContext, makeModelSubmodelYear: attributesVehicle });
    const hasLinkWidget = getHasLinkWidget(urlContext);
    const showFilmstripAd = !!(selectedFacets.make && selectedFacets.model);
    const cardListProps = {
      cpoData,
      visitorLocation,
      paymentType,
      selectedPaymentType,
      radius,
      isNational,
      isDealerSrp,
      isMobile,
      disableInventoryMLPhotoSort,
      cardComponent: UsurpInventoryCard,
      grayIcons: true,
      isEnhancedName: true,
      showNationalDelivery: isNational,
      isSrp: true,
      openLinksInNewTab: true,
      cardKeyPrefix: inventoryData?.hash,
      disableDrivewayNewCarLeadForms,
      inventory,
      inventoryCount,
      isCrawler,
    };

    const lowInventoryModule = displayLowInvModule ? (
      <InventoriesNotFoundWithLinks
        attributes={attributes}
        selectedFacets={selectedFacets}
        inventoriesCount={inventoryCount}
        onUpdate={onUpdateFilter}
        onZipCodeUpdate={onUpdateZipCode}
        withLeadForm
      />
    ) : null;

    const stickyHeaderProps = {
      seoData,
      attributes,
      visitorLocation,
      facets,
      selectedFacets,
      suggestedFacets,
      filteringByFacets,
      onFiltersBtnClick: onOpenFiltersDrawer,
      onUpdate: isMobile ? onSuggestedFacetUpdate : onUpdateFilter,
      isNational,
      isDealerSrp,
      isMobile,
      onApplyFilterToUrl,
    };

    const facetsProps = {
      paymentType: selectedPaymentType,
      facets,
      filteringByFacets,
      onUpdate: onUpdateFilter,
      zipCode,
      onZipCodeUpdate: onUpdateZipCode,
      isNational,
      isDealerSrp,
      selectedFacets,
      setIsMultiMM: onMultiMMChange,
      searchResultsFound: inventoryCount,
      pagePublicationState,
      isShop5157Challenger,
      hasBuyOnlineInventories,
    };

    const breadcrumbsData = getSrpBreadcrumbs({
      seoData,
      vehicle: attributes.vehicle,
      routeParams: { year, submodel },
      pathName,
    });

    const cardsTopPanelSelectedFacets =
      !get(selectedFacets, 'sortBy') && isExpandedRadiusDueToLowResultsSort
        ? { ...selectedFacets, [SORT_BY]: [expandedRadiusDueToLowResultsSort] }
        : selectedFacets;

    const srpCardsTopPanel = (
      <SrpCardsTopPanel
        onUpdate={onUpdateFilter}
        inventoryCount={inventoryCount}
        isMobile={isMobile}
        withRange={!!facets?.find(({ type }) => type === ELECTRICITY_RANGE)}
        selectedFacets={cardsTopPanelSelectedFacets}
        className={
          isMobile && shop5067ExperimentVariant === 'chal1' ? 'shop-5067-srp-top-cards-top-panel-chal1' : undefined
        }
      />
    );

    const shop5067SrpCardsTopPanelChal2 = (
      <SrpCardsTopPanel
        onUpdate={onUpdateFilter}
        inventoryCount={inventoryCount}
        isMobile
        withRange={!!facets?.find(({ type }) => type === ELECTRICITY_RANGE)}
        selectedFacets={cardsTopPanelSelectedFacets}
        className="shop-5067-srp-top-cards-top-panel mb-0 flex-shrink-0"
      />
    );

    const renderNumberLinkComponent = useCallback(
      ({ pageNumber: linkPageNumber, rel, ...rest }) => {
        const MAX_INDEXED_PAGE_NUMBER = 5;

        const PaginationComponent = isSeot4897Enabled ? PaginationButton : PaginationLink;
        return <PaginationComponent {...rest} rel={linkPageNumber > MAX_INDEXED_PAGE_NUMBER ? 'nofollow' : rel} />;
      },
      [isSeot4897Enabled]
    );

    const paginationComponent = useMemo(
      () => (
        <SrpPagination
          pageNumber={pageNumber}
          pagesCount={totalPages}
          maxAllowedPageNumber={maxAllowedPageNumber}
          pageSize={pageSize}
          pageResultsCount={inventory.length}
          inventoryCount={inventoryCount}
          selectedFacets={selectedFacets}
          urlContext={urlContext}
          pathName={pathName}
          onPageLinkClick={onPageChange}
          isMobile={isMobile}
          renderNumberLinkComponent={renderNumberLinkComponent}
          {...{ linkComponent: isSeot4897Enabled ? PaginationButton : PaginationLink }}
        />
      ),
      [
        pageNumber,
        totalPages,
        maxAllowedPageNumber,
        pageSize,
        inventory.length,
        inventoryCount,
        selectedFacets,
        urlContext,
        pathName,
        onPageChange,
        isMobile,
        renderNumberLinkComponent,
        isSeot4897Enabled,
      ]
    );

    const shop5016BackfillControlVariant = (
      <>
        <ZipLongDistanceConfirmProvider>
          <UsurpCardList {...cardListProps} />
        </ZipLongDistanceConfirmProvider>
        {lowInventoryModule}
        {paginationComponent}
      </>
    );

    return (
      <Container className="pb-3">
        <FeatureFlag name="disable-srp-llm">
          {disableLlm =>
            isVinPreview ? (
              <UsurpStickyHeaderSemanticSearch
                {...stickyHeaderProps}
                disableLlm={disableLlm}
                isVinPreview
                shop5067ExperimentVariant="ctrl"
              />
            ) : (
              <div className="shop-5067-srp-top-mobile-header">
                <Experiment name="shop-5067-srp-top" showDefault>
                  <Recipe name="ctrl" isDefault>
                    <UsurpStickyHeaderSemanticSearch
                      {...stickyHeaderProps}
                      disableLlm={disableLlm}
                      isVinPreview={isVinPreview}
                      shop5067ExperimentVariant="ctrl"
                    />
                  </Recipe>
                  <Recipe name="chal1">
                    <UsurpStickyHeaderSemanticSearch
                      {...stickyHeaderProps}
                      disableLlm={disableLlm}
                      isVinPreview={isVinPreview}
                      shop5067ExperimentVariant="chal1"
                    />
                  </Recipe>
                  <Recipe name="chal2">
                    <UsurpStickyHeaderSemanticSearch
                      {...stickyHeaderProps}
                      disableLlm={disableLlm}
                      isVinPreview={isVinPreview}
                      shop5067ExperimentVariant="chal2"
                    />
                  </Recipe>
                </Experiment>
              </div>
            )
          }
        </FeatureFlag>
        <Row className="flex-row-reverse">
          <Col
            xs={12}
            lg={9}
            role="region"
            aria-label="Listings"
            tabIndex={-1}
            id={SKIP_TO_LINKS_CONFIG.RESULTS_CONTAINER_ID}
          >
            {!isMobile && (
              <ScrollLink
                to={SKIP_TO_LINKS_CONFIG.FILTERS_CONTAINER_ID}
                className="visually-hidden-focusable d-none d-lg-block"
                scrollConfig={{ offset: GLOBAL_HEADER_OFFSET }}
              >
                {SKIP_TO_LINKS_CONFIG.FILTERS_CONTAINER_LABEL}
              </ScrollLink>
            )}

            {!isMobile && srpCardsTopPanel}

            {isMobile && isUsed && hasBuyOnlineInventories && isShop5067SrpTopChallenger && (
              <SrpBuyOnlinePromo selectedFacets={selectedFacets} />
            )}

            {isMobile && shop5067ExperimentVariant === 'chal1' && (
              <div className="shop-5067-srp-top-toolbar d-flex align-items-center justify-content-between px-1 py-0_5 mb-0 bg-white">
                <FilterButton
                  onFiltersBtnClick={onOpenFiltersDrawer}
                  activeFiltersCount={activeFiltersCount}
                  classes="d-flex flex-grow-1 justify-content-start"
                />
                <SrpLocalListingsCheckbox selectedFacets={selectedFacets} onUpdate={onUpdateFilter} />
              </div>
            )}

            {isMobile && shop5067ExperimentVariant === 'chal2' && (
              <div className="shop-5067-srp-top-toolbar shop-5067-srp-top-toolbar-chal2 d-flex align-items-center justify-content-between px-1 py-0_5 bg-white">
                <FilterButton
                  onFiltersBtnClick={onOpenFiltersDrawer}
                  activeFiltersCount={activeFiltersCount}
                  classes="d-flex justify-content-start"
                />
                {shop5067SrpCardsTopPanelChal2}
              </div>
            )}

            <ZipCodeReminder
              onZipCodeUpdate={onUpdateZipCode}
              isNational={isNational}
              isMobile={isMobile}
              zipCode={zipCode}
            />
            {isMobile && isUsed && hasBuyOnlineInventories && !isShop5067SrpTopChallenger && (
              <EvEBuyingExperienceButtonGroup
                selectedFacets={selectedFacets}
                isMobile={isMobile}
                selectedBtn={selectedBtn}
              />
            )}

            {dealerId && (
              <div className="pt-lg-0 pb-lg-2">
                <IcoReducedHeightStandard dealerId={dealerId} IcoWidgetComponent={IcoWidget} decreaseTitle />
              </div>
            )}
            <SrpTopMessage
              selectedFacets={selectedFacets}
              inventoryData={inventoryData}
              pathName={pathName}
              pageName={pageName}
              isLocalDeliveryType={isLocalDeliveryType}
              isExpandedRadiusDueToLowResults={isExpandedRadiusDueToLowResults}
              hasCpoMessage={cpoData?.hasCpoMessage}
              isMobile={isMobile}
              userCapOnePrequalApproved={userCapOnePrequalApproved}
              onUpdate={onUpdateFilter}
              onRemoveCapOnePrequalParam={onRemoveCapOnePrequalParam}
              zipCode={zipCode}
              paymentType={paymentType}
              isCrawler={isCrawler}
              isNational={isNational}
            />

            {isMobile && shop5067ExperimentVariant !== 'chal2' && srpCardsTopPanel}

            {pathName === SRP_BASE_PATHNAME ? (
              <Experiment name={SHOP_5016_CAMPAIGN} showDefault>
                <Recipe name="ctrl" isDefault>
                  {shop5016BackfillControlVariant}
                </Recipe>
                <Recipe name={SHOP_5016_STRATEGIES.RADIUS}>
                  <Shop5016Backfill
                    strategy={SHOP_5016_STRATEGIES.RADIUS}
                    cardListProps={cardListProps}
                    fallback={lowInventoryModule}
                    paginationComponent={paginationComponent}
                  />
                </Recipe>
                <Recipe name={SHOP_5016_STRATEGIES.SIMILAR}>
                  <Shop5016Backfill
                    strategy={SHOP_5016_STRATEGIES.SIMILAR}
                    cardListProps={cardListProps}
                    fallback={lowInventoryModule}
                    paginationComponent={paginationComponent}
                  />
                </Recipe>
              </Experiment>
            ) : (
              shop5016BackfillControlVariant
            )}

            {!!inventoryCount && (
              <FeatureFlag name="enable-codriver-banner">
                <IgnoreRenderWhenCoDriver>
                  <Experiment name="search-534-srp-codriver" showDefault>
                    <Recipe name="ctrl" isDefault>
                      <CoDriverBanner />
                    </Recipe>
                    <Recipe name="chal">
                      <CoDriverBanner isSearch534Chal />
                    </Recipe>
                  </Experiment>
                </IgnoreRenderWhenCoDriver>
              </FeatureFlag>
            )}

            <Experiment name="ads-12460_dfp" showDefault>
              <Recipe name="ctrl" isDefault>
                <MinHeightWrapper componentType="SPONSORED_CARS" className="mt-1 pt-1">
                  <SponsoredCars showLargeArrows showBottomDividingLine={false} />
                </MinHeightWrapper>
              </Recipe>
              <Recipe name="chal">
                <MinHeightWrapper
                  componentType="SPONSORED_CARS_STACKED"
                  className="mt-1 pt-1"
                  style={isMobile ? null : { maxWidth: '744px' }}
                >
                  <SponsoredCars showLargeArrows showBottomDividingLine={false} showStackedAds />
                </MinHeightWrapper>
              </Recipe>
            </Experiment>
            <Breadcrumbs data={breadcrumbsData} className="p-0 my-1" />
            {isMobile && (
              <Fragment>
                <div className="mt-1 d-flex">
                  <InventoryAdTargetingPrefetch>
                    <MrectNative wrapperClass="mrect mx-auto" mobileOnly refreshable={false} useMinHeight />
                  </InventoryAdTargetingPrefetch>
                </div>
                {showFilmstripAd && (
                  <Experiment name="ads-12460_dfp" showDefault>
                    <Recipe name="ctrl" isDefault>
                      <InventoryAdTargetingPrefetch>
                        <div className="my-1">
                          <NativeAd
                            wrapperClass="mx-auto filmstrip"
                            nativeStyle="filmstrip"
                            position={'0'}
                            refreshable
                            noRefresh={['scroll', 'engagement']}
                            mobileOnly
                            useMinHeight
                          />
                        </div>
                      </InventoryAdTargetingPrefetch>
                    </Recipe>
                    <Recipe name="chal" />
                  </Experiment>
                )}
              </Fragment>
            )}
            <ResearchPageLink vehicle={attributesVehicle} inventory={inventory} selectedFacets={selectedFacets} />
            {!!consumerReviews && !!attributesVehicle && (
              <ConsumerReviewsWidget
                data={consumerReviews}
                vehicle={attributesVehicle}
                latestYear={latestYear}
                isMobile={isMobile}
              />
            )}
            {Boolean(isCpo(inventoryType) && make) && <CertifiedPreOwned make={make} showMainHeader className="mt-2" />}
            {Boolean(greatDealsWidgetData) && (
              <GreatDealsLinkWidgetTable data={greatDealsWidgetData} isMobile={isMobile} className="mt-2" />
            )}
            {Boolean(leaseDealsWidgetData) && (
              <LeaseDealsLinkWidgetTable make={make} data={leaseDealsWidgetData} isMobile={isMobile} className="mt-2" />
            )}
            {Boolean(isNewFlatUrlSRP(urlPath) && make) && <InventoryDataTable inventory={inventory} className="mt-2" />}
            {hasFaqApiData && (
              <ErrorBoundary>
                <FaqModule className="mt-2" faqApiData={faqApiData} />
              </ErrorBoundary>
            )}
            {isUsedYMMCityStateSRP(urlPath) && (
              <AppraisalSection
                urlContext={urlContext}
                appraisalWidgetInfo={usedYearAppraisalWidget}
                isMobile={isMobile}
              />
            )}
          </Col>
          {!isMobile && (
            <Col lg={3} className="d-none d-lg-block filters-column">
              <ErrorBoundary>
                <UsurpFiltersPanel>
                  <UsurpFilters {...facetsProps} />
                </UsurpFiltersPanel>
              </ErrorBoundary>
              <div className="adsol-mrect">
                <div className="first-mrect-wrapper hidden-xl-down">
                  <InventoryAdTargetingPrefetch>
                    <MrectNative
                      refreshable
                      noRefresh={['scroll', 'engagement']}
                      wiredBreakpoints={{
                        xl: true,
                      }}
                      position={'1'}
                    />
                  </InventoryAdTargetingPrefetch>
                </div>
              </div>
              {showFilmstripAd && (
                <Experiment name="ads-12460_dfp" showDefault>
                  <Recipe name="ctrl" isDefault>
                    <InventoryAdTargetingPrefetch>
                      <NativeAd
                        wrapperClass="my-2_5 filmstrip"
                        nativeStyle="filmstrip"
                        position={'0'}
                        refreshable
                        noRefresh={['scroll', 'engagement']}
                        wiredOnly
                      />
                    </InventoryAdTargetingPrefetch>
                  </Recipe>
                  <Recipe name="chal" />
                </Experiment>
              )}
              <div className="adsol-mrect">
                <div className="second-mrect-wrapper hidden-xl-down">
                  <InventoryAdTargetingPrefetch>
                    <MrectNative
                      position={'2'}
                      refreshable={false}
                      wiredBreakpoints={{
                        xl: true,
                      }}
                      style={{ minHeight: '600px' }}
                      verticalAlign={VERTICAL_ALIGN.TOP}
                      addCustomSizesWired={MRECT_WIRED_CUSTOM_SIZE}
                    />
                  </InventoryAdTargetingPrefetch>
                </div>
              </div>
            </Col>
          )}
        </Row>
        {hasLinkWidget && <hr className="mt-1_5 mb-2" />}
        {hasLinkWidget && (
          <PageLinksWidget
            divideSections={false}
            title={linkWidgetTitle}
            pageName={pageName}
            vehicle={attributesVehicle}
            className="mt-2 pb-3"
            inventory={inventory}
          />
        )}
        <SrpDisclaimer inventory={inventory} />
        {[PUB_STATES_LOWERCASE.USED, PUB_STATES_LOWERCASE.NEW_USED].includes(pagePublicationState) && (
          <Experiment name="TED-7106-CarZing-Leads-v4" showDefault>
            <Recipe name="ctrl" isDefault />
            <Recipe name="chal-0" />
            <Recipe name="chal-1" />
            <Recipe name="chal-2" />
            <Recipe name="chal-3" />
          </Experiment>
        )}

        <Shop3947Experiment />

        {[PUB_STATES_LOWERCASE.USED, PUB_STATES_LOWERCASE.NEW_USED].includes(pagePublicationState) && (
          <Experiment name="TED-7440-Basic-Listing-Lead-Vaporware" showDefault>
            <Recipe name="ctrl" isDefault />
            <Recipe name="chal-0" />
            <Recipe name="chal-1" />
          </Experiment>
        )}
      </Container>
    );
  }
);

UsurpMainContent.displayName = 'UsurpMainContent';

UsurpMainContent.propTypes = {
  inventoryData: PropTypes.shape({
    inventories: PropTypes.shape({
      results: InventoryEntities.InventoriesVin,
      totalNumber: PropTypes.number,
      totalPages: PropTypes.number,
    }),
    facets: InventoryEntities.Facets,
    seoInfo: PropTypes.shape({}),
  }).isRequired,
  radius: PropTypes.number.isRequired,
  isMobile: PropTypes.bool.isRequired,
  selectedFacets: PropTypes.shape({}).isRequired,
  urlContext: PropTypes.shape({}),
  zipCode: PropTypes.string.isRequired,
  location: PropTypes.shape({}),
  visitorLocation: VisitorEntities.Location,
  disableInventoryMLPhotoSort: PropTypes.bool,
  isNational: PropTypes.bool,
  paymentType: PropTypes.string,
  selectedPaymentType: PropTypes.string,
  isDealerSrp: PropTypes.bool,
  facets: PropTypes.arrayOf(PropTypes.shape({})),
  seoData: SearchFilterSeoObjectEntities.isRequired,
  filteringByFacets: PropTypes.arrayOf(PropTypes.shape({})),
  onOpenFiltersDrawer: PropTypes.func.isRequired,
  onSuggestedFacetUpdate: PropTypes.func.isRequired,
  onUpdateFilter: PropTypes.func.isRequired,
  onUpdateZipCode: PropTypes.func.isRequired,
  pathName: PropTypes.string,
  userCapOnePrequalApproved: PropTypes.bool,
  onRemoveCapOnePrequalParam: PropTypes.func.isRequired,
  onMultiMMChange: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  year: PropTypes.string,
  submodel: PropTypes.string,
  isVinPreview: PropTypes.bool,
  disableDrivewayNewCarLeadForms: PropTypes.bool,
  cpoData: PropTypes.shape({
    hasCpoMessage: PropTypes.bool,
    oemLogo: PropTypes.string,
  }),
  isCrawler: PropTypes.bool,
  onApplyFilterToUrl: PropTypes.func,
  isSeot4897Enabled: PropTypes.bool,
  isShop5157Challenger: PropTypes.bool,
};
